import React, { FC, ReactElement, ReactNode, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton, IgTooltip } from "@intelligems/ig-theme";
import { IntelligemsAppScopeType } from "@intelligems/ig-types";
import LockIcon from "@mui/icons-material/Lock";
import { ButtonProps } from "@mui/material";

import { useIsAdmin } from "../../api/user-auths/is-admin";
import { setDialog } from "../../store/slices/global";

import { useHasRequiredShopifyScopes } from "./hooks";
import { ADMINS_HAVE_FULL_ACCESS } from "./Scopes";

interface LimitedAccessButtonProps extends ButtonProps {
  isEnabled: boolean;
  children: ReactNode;
  debugId: string;
  customDisabledTooltipMessage?: ReactNode;
  requiredShopifyScopes?: IntelligemsAppScopeType[];
  loading?: boolean;
  modalPopupProps?: {
    title: string;
    message: string;
    yesButtonText: string;
    noButtonText: string;
    yesButtonAction: any;
    noButtonAction: any;
  };
  adminDisable?: boolean; // when a component should be disabled for admins too
  override?: "enabled" | "disabled";
  customTooltip?: ReactElement;
  customColor?: string;
  customBackgroundColor?: string;
}

export const ScopedButton: FC<LimitedAccessButtonProps> = ({
  isEnabled,
  loading,
  children,
  adminDisable,
  override,
  customTooltip,
  debugId,
  customColor,
  customBackgroundColor,
  customDisabledTooltipMessage,
  requiredShopifyScopes,
  modalPopupProps,
  ...props
}) => {
  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const hasRequiredShopifyScopes = useHasRequiredShopifyScopes(
    requiredShopifyScopes || []
  );
  const doAdminsHaveAccess = isAdmin && ADMINS_HAVE_FULL_ACCESS;

  const [previousEnabled, setPreviousEnabled] = useImmer<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    if (previousEnabled === undefined) {
      setPreviousEnabled(isEnabled);
    }
  }, [isEnabled]);

  useEffect(() => {
    if (
      previousEnabled == true &&
      !doAdminsHaveAccess &&
      !isEnabled &&
      modalPopupProps
    ) {
      setPreviousEnabled(isEnabled);
      dispatch(
        setDialog({
          dialog: "scoped",
          open: true,
          props: {
            title: modalPopupProps.title,
            message: modalPopupProps.message,
            yesButtonText: modalPopupProps.yesButtonText,
            yesButtonAction: modalPopupProps.yesButtonAction,
            noButtonText: modalPopupProps.noButtonText,
            noButtonAction: modalPopupProps.noButtonAction,
          },
        })
      );
    }
  }, [isEnabled]);

  if (!hasRequiredShopifyScopes) {
    return (
      <IgButton
        {...props}
        onClick={() => {
          dispatch(setDialog({ dialog: "missingShopifyScope", open: true }));
        }}
      >
        {children}
      </IgButton>
    );
  }

  if (doAdminsHaveAccess && !adminDisable) {
    return <IgButton {...props}>{children}</IgButton>;
  }

  return (
    <IgTooltip
      title={
        isEnabled
          ? ""
          : customDisabledTooltipMessage ||
            "Please contact Intelligems to unlock this feature"
      }
    >
      <span>
        <IgButton
          startIcon={!isEnabled && <LockIcon />}
          disabled={!isEnabled}
          {...props}
        >
          {children}
        </IgButton>
      </span>
    </IgTooltip>
  );
};
